import { useState } from 'react'

import NiceModal from '@ebay/nice-modal-react'
import { Group, Stack } from '@mantine/core'

import { FaIcon } from '#components/FaIcon'
import { OwnerSelect } from '#components/Settings/Members/OwnerSelect'
import { Toast } from '#components/Toast'
import { IOrg, useMst } from '#state'

import { ModalShell } from './ModalShell'
import { useModalControls } from './useModalControls'

// See (./readme.md)
// oxlint-disable-next-line react/only-export-components
const TransferOwnershipModalInternal = ({ workspace }: { workspace: IOrg }) => {
  const { doDebug } = useMst()

  const controls = useModalControls()
  const [orgData, setOrgData] = useState<{
    canSend: boolean
    ownerId: string
  }>({
    canSend: false,
    ownerId: workspace.owner?.id ?? '',
  })

  const handleConfirm = async () => {
    controls.setLoading(true)
    try {
      await doDebug()
      await workspace.update({ ownerId: orgData.ownerId })
      controls.onClose()
    } catch {
      controls.setErrorMessage('Failed to assign new owner')
    } finally {
      controls.setLoading(false)
      setOrgData({
        canSend: false,
        ownerId: orgData.ownerId,
      })
    }
  }

  return (
    <ModalShell
      title="Transfer Ownership"
      confirmLabel="Transfer Ownership"
      cancelLabel="Cancel"
      onConfirm={handleConfirm}
      opened={controls.opened}
      loading={controls.loading}
      disabled={!orgData.canSend}
      errorMessage={controls.errorMessage}
      onClose={controls.onClose}
      dangerous={true}
    >
      <Stack justify="flex-start" gap="md">
        <Toast
          p="xs"
          type="warning"
          message={
            <Group wrap="nowrap" gap="sm">
              <FaIcon icon="fa-exclamation-triangle" faIconSet="fa" size="20" />
              <div>
                <strong>You currently own this workspace. </strong>
                <span>
                  If you transfer ownership, the new owner will be able to
                  change your role, your permissions, and your membership
                  status.
                </span>
              </div>
            </Group>
          }
          dismissable={false}
        ></Toast>
        <OwnerSelect org={workspace} onChange={(obj) => setOrgData(obj)} />
      </Stack>
    </ModalShell>
  )
}

const TransferOwnershipModal = NiceModal.create(TransferOwnershipModalInternal)

export const showTransferOwnershipModal = (workspace: IOrg) => {
  NiceModal.show(TransferOwnershipModal, { workspace })
}
